import React, { useState } from 'react';
import { X, BarChart3, Calendar, FileText, Save, Download, TrendingUp } from 'lucide-react';

interface CreateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reportData: any) => void;
}

const CreateReportModal: React.FC<CreateReportModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    type: 'monthly' as 'weekly' | 'monthly' | 'quarterly' | 'custom',
    startDate: '',
    endDate: '',
    includeCharts: true,
    includeDetails: true,
    includeComparison: false,
    sendEmail: false,
    emailRecipients: '',
    format: 'pdf' as 'pdf' | 'excel' | 'both'
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    onSave({
      ...formData,
      id: `report-${Date.now()}`,
      createdAt: new Date(),
      status: 'generated'
    });
    
    setIsSaving(false);
    onClose();
  };

  const getDateRange = () => {
    const now = new Date();
    switch (formData.type) {
      case 'weekly':
        const weekStart = new Date(now.setDate(now.getDate() - 7));
        return {
          start: weekStart.toISOString().split('T')[0],
          end: new Date().toISOString().split('T')[0]
        };
      case 'monthly':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          start: monthStart.toISOString().split('T')[0],
          end: new Date().toISOString().split('T')[0]
        };
      case 'quarterly':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        return {
          start: quarterStart.toISOString().split('T')[0],
          end: new Date().toISOString().split('T')[0]
        };
      default:
        return { start: '', end: '' };
    }
  };

  React.useEffect(() => {
    if (formData.type !== 'custom') {
      const range = getDateRange();
      setFormData(prev => ({
        ...prev,
        startDate: range.start,
        endDate: range.end
      }));
    }
  }, [formData.type]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Rapor Oluştur</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Rapor Bilgileri */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Rapor Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rapor Başlığı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Örn: Ocak 2024 Satış Performans Raporu"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rapor Türü <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="weekly">Haftalık Rapor</option>
                  <option value="monthly">Aylık Rapor</option>
                  <option value="quarterly">Çeyreklik Rapor</option>
                  <option value="custom">Özel Tarih Aralığı</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Format
                </label>
                <select
                  name="format"
                  value={formData.format}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="pdf">PDF</option>
                  <option value="excel">Excel</option>
                  <option value="both">PDF + Excel</option>
                </select>
              </div>

              {formData.type === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Başlangıç Tarihi <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bitiş Tarihi <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Rapor İçeriği */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Rapor İçeriği</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  id="includeCharts"
                  type="checkbox"
                  name="includeCharts"
                  checked={formData.includeCharts}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="includeCharts" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Grafikler ve Çizelgeler
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="includeDetails"
                  type="checkbox"
                  name="includeDetails"
                  checked={formData.includeDetails}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="includeDetails" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Detaylı Analiz
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="includeComparison"
                  type="checkbox"
                  name="includeComparison"
                  checked={formData.includeComparison}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="includeComparison" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Önceki Dönem Karşılaştırması
                </label>
              </div>
            </div>
          </div>

          {/* Email Gönderimi */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Email Gönderimi</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="sendEmail"
                  type="checkbox"
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="sendEmail" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Raporu email ile gönder
                </label>
              </div>
              
              {formData.sendEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Adresleri (virgülle ayırın)
                  </label>
                  <textarea
                    name="emailRecipients"
                    value={formData.emailRecipients}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${
                isSaving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  : 'bg-yellow-600 hover:bg-yellow-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-yellow-600/25'
              }`}
            >
              <TrendingUp className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
              <span>{isSaving ? 'Oluşturuluyor...' : 'Rapor Oluştur'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReportModal;